/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  BuilderPermission,
  LetterTemplateLevel,
  LetterTemplateType,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { find, get } from 'lodash';
import {
  isCorporateExist,
  isCorporateUsed,
  isDraftExist,
  isMasterExist,
  isMasterUsed,
  isOfficeExist,
  isOfficeUsed,
  LetterTemplate,
} from '../../../resources/letter-template.model';

export interface LetterTemplateEvent {
  template: LetterTemplate;
}

export interface LetterTemplateCopyFromEvent extends LetterTemplateEvent {
  source: LetterTemplateLevel;
}

export interface LetterTemplateValidateEvent extends LetterTemplateEvent {
  level: LetterTemplateLevel;
  isDraft: boolean;
}

export interface LetterTemplateLocationModel {
  id: ID;
  level?: LetterTemplateLevel;
  exists: boolean;
  used: boolean;
  enabled: boolean;
  label: string;
  copyToDraft?: boolean;
  copyToOwn?: boolean;
  linkToDoc?: string;
  pushDown?: boolean;
  validate?: boolean;
  draft?: boolean;
}

@Component({
  standalone: false,
  selector: 'gh-lt-location-management',
  templateUrl: 'lt-location-management.component.html',
  styleUrls: ['lt-location-management.component.scss'],
})
export class LtLocationManagementComponent {
  @Input() type: LetterTemplateType;
  @Input() editable: boolean = false;

  @Output() copyToOwn = new EventEmitter<LetterTemplateEvent>();
  @Output() copyToDraft = new EventEmitter<LetterTemplateCopyFromEvent>();
  @Output() pushDown = new EventEmitter<LetterTemplateEvent>();
  @Output() validate = new EventEmitter<LetterTemplateValidateEvent>();

  locations: LetterTemplateLocationModel[];
  selectedId?: ID;

  private _template?: LetterTemplate;

  constructor(public userContext: BuilderUserContext) {

  }

  @Input()
  set template(template: Maybe<LetterTemplate>) {
    const { _template: assignedTemplate } = this;
    const isSameTemplate = assignedTemplate?.uniqueId === template?.uniqueId;
    this._template = template;
    this.locations = this.buildLocations();
    this.selectedId = isSameTemplate ? this.selectedId : get(find(this.locations, { enabled: true }), 'id');
  }

  get template(): Maybe<LetterTemplate> {
    return this._template;
  }

  get selected(): Maybe<LetterTemplateLocationModel> {
    const { selectedId } = this;
    return selectedId ? find(this.locations, { id: selectedId }) : void 0;
  }

  get hasManagePermission(): boolean {
    switch (this.type) {
      case LetterTemplateType.System:
        return this.userContext.hasPermission(BuilderPermission.Setup.ManageTemplates.SystemLetters.MANAGE);
      case LetterTemplateType.Job:
        return this.userContext.hasPermission(BuilderPermission.Setup.ManageTemplates.JobLetters.MANAGE);
      default:
        return false;
    }
  }

  // Link should not be available in sandbox mode
  getDocumentLink(location: LetterTemplateLocationModel): Maybe<string> {
    return this.userContext.sandbox ? void 0 : location.linkToDoc;
  }

  getMarkerClass(exists: boolean, used: boolean): string {
    if (exists) {
      if (used) {
        return 'marker-used';
      } else {
        return 'marker-exists';
      }
    } else {
      return 'marker-not-exist';
    }
  }

  onCopyToDraft(): void {
    const { selected, template } = this;
    if (template && selected) {
      this.copyToDraft.emit({ template, source: <LetterTemplateLevel>selected.level });
    }
  }

  onCopyToOwn(): void {
    const { selected, template } = this;
    if (template && selected) {
      this.copyToOwn.emit({ template });
    }
  }

  onPushDown(): void {
    const { template } = this;
    if (template) {
      this.pushDown.emit({ template });
    }
  }

  onValidate(location: LetterTemplateLocationModel): void {
    const { template } = this;
    if (template) {
      this.validate.emit({ template, level: location.level!, isDraft: !!location.draft });
    }
  }

  private buildLocations(): LetterTemplateLocationModel[] {
    const { editable } = this;
    const { template } = this;

    const existsCorporate = template ? isCorporateExist(template) : false;
    const existsMaster = template ? isMasterExist(template) : false;
    const existsOffice = template ? isOfficeExist(template) : false;
    const existsDraft = template ? isDraftExist(template) : false;

    const usedCorporate = template ? isCorporateUsed(template) : false;
    const usedMaster = template ? isMasterUsed(template) : false;
    const usedOffice = template ? isOfficeUsed(template) : false;

    if (this.userContext.officeView) {
      return [{
        id: 'corporate',
        level: LetterTemplateLevel.Corporate,
        enabled: editable && existsCorporate,
        exists: existsCorporate,
        used: usedCorporate,
        copyToDraft: true,
        label: 'lbl_corporate_templates',
      }, {
        id: 'master',
        level: LetterTemplateLevel.Master,
        enabled: editable && existsMaster,
        exists: existsMaster,
        used: usedMaster,
        copyToDraft: true,
        label: 'lbl_master_templates',
      }, {
        id: 'draft',
        enabled: editable && existsDraft,
        exists: existsDraft,
        used: false,
        copyToOwn: true,
        linkToDoc: template && template.draftDocUrl,
        label: 'lbl_office_templates_draft',
        validate: true,
        draft: true,
      }, {
        id: 'office',
        level: LetterTemplateLevel.Office,
        enabled: false,
        exists: existsOffice,
        used: usedOffice,
        label: 'lbl_office_templates',
        validate: true,
      }];
    } else if (this.userContext.masterView) {
      return [{
        id: 'corporate',
        level: LetterTemplateLevel.Corporate,
        enabled: editable && existsCorporate,
        exists: existsCorporate,
        used: usedCorporate,
        copyToDraft: true,
        label: 'lbl_corporate_templates',
      }, {
        id: 'draft',
        enabled: editable && existsDraft,
        exists: existsDraft,
        used: false,
        linkToDoc: template && template.draftDocUrl,
        copyToOwn: true,
        label: 'lbl_master_templates_draft',
        validate: true,
        draft: true,
      }, {
        id: 'master',
        level: LetterTemplateLevel.Master,
        enabled: editable && existsMaster,
        exists: existsMaster,
        used: usedMaster,
        pushDown: true,
        label: 'lbl_master_templates',
        validate: true,
      }];
    } else {
      return [{
        id: 'draft',
        enabled: editable && existsDraft,
        exists: existsDraft,
        used: false,
        linkToDoc: template && template.draftDocUrl,
        copyToOwn: true,
        label: 'lbl_corporate_templates_draft',
        validate: true,
        draft: true,
      }, {
        id: 'corporate',
        level: LetterTemplateLevel.Corporate,
        enabled: editable && existsCorporate,
        exists: existsCorporate,
        used: usedCorporate,
        copyToDraft: true,
        pushDown: true,
        label: 'lbl_corporate_templates',
        validate: true,
      }];
    }
  }
}
